import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';
import { CompanyEntity, CreateCompanyDto, UpdateCompanyDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, CompanyStatus } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';

@Injectable()
export class CompaniesService {
  private readonly logger = new Logger(CompaniesService.name);

  constructor(
    @InjectRepository(CompanyEntity)
    private readonly companyRepo: Repository<CompanyEntity>,
  ) {}

  // Get all companies
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const companies = await this.companyRepo.find({
        relations: [
          'created_by', 
          'updated_by', 
          'attachment_company_logo', 
          'attachment_org_chart'
        ],
      });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.GET_ALL.MESSAGE,
        data: companies,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.COMPANY.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get companies by user id (creator)
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const companies = await this.companyRepo.find({
        where: { created_by: { id: userId } },
        relations: [
          'created_by', 
          'updated_by', 
          'attachment_company_logo', 
          'attachment_org_chart'
        ],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.GET_BY_ID.MESSAGE,
        data: companies,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.COMPANY.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Get company by id
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const company = await this.companyRepo.findOne({
        where: { id },
        relations: [
          'created_by', 
          'updated_by', 
          'attachment_company_logo', 
          'attachment_org_chart'
        ],
      });

      if (!company) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.COMPANY.GET_BY_ID.CODE,
          message: 'Company not found',
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.GET_BY_ID.MESSAGE,
        data: company,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.COMPANY.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Create a new company
  async create(dto: CreateCompanyDto): Promise<ApiResponseInterface> {
    try {
      const newCompany = this.companyRepo.create(dto);
      await this.companyRepo.save(newCompany);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.CREATE.MESSAGE,
        data: newCompany,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.CREATE.CODE,
        message: API_RESPONSE.ERROR.COMPANY.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // Update existing company
  async update(dto: UpdateCompanyDto): Promise<ApiResponseInterface> {
    try {
      const company = await this.companyRepo.findOne({
        where: { id: dto.id },
        relations: [
          'updated_by', 
          'attachment_company_logo', 
          'attachment_org_chart'
        ],
      });
      if (!company) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.COMPANY.UPDATE.CODE,
          message: 'Company not found',
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto, {
        updated_by_id: 'updated_by',
        attachment_company_logo_id: 'attachment_company_logo',
        attachment_org_chart_id: 'attachment_org_chart',
      });

      Object.assign(company, updateData);
      await this.companyRepo.save(company);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.UPDATE.MESSAGE,
        data: company,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.UPDATE.CODE,
        message: API_RESPONSE.ERROR.COMPANY.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // Soft delete (set status to inactive or deleted)
  async softDelete(dto: UpdateCompanyDto): Promise<ApiResponseInterface> {
    try {
      const company = await this.companyRepo.findOne({ where: { id: dto.id } });
      if (!company) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.COMPANY.DELETE.CODE,
          message: 'Company not found',
          data: null,
        };
      }

      company.status = CompanyStatus.DELETED;
      await this.companyRepo.save(company);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.COMPANY.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.COMPANY.DELETE.MESSAGE,
        data: company,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.COMPANY.DELETE.CODE,
        message: API_RESPONSE.ERROR.COMPANY.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}